import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { MotionConfig } from "framer-motion";

type MotionPref = "system" | "reduce" | "no-preference";

interface MotionPreferenceCtx {
  shouldReduceMotion: boolean;
  preference: MotionPref;
  toggle: () => void;
  setPreference: (pref: MotionPref) => void;
}

const Ctx = createContext<MotionPreferenceCtx>({
  shouldReduceMotion: false,
  preference: "system",
  toggle: () => {},
  setPreference: () => {},
});

const STORAGE_KEY = "alchema-motion-pref";
const MQ = "(prefers-reduced-motion: reduce)";

function getStoredPref(): MotionPref {
  if (typeof window === "undefined") return "system";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "reduce" || stored === "no-preference") return stored;
  return "system";
}

export function MotionPreferenceProvider({
  children,
}: {
  children: ReactNode;
}) {
  // Start with SSR-safe values. Reading localStorage or matchMedia during the
  // client hydration render can choose a different MotionConfig/tree from the
  // server and turn a saved reduced-motion preference into a hydration error.
  const [preference, setPreferenceState] = useState<MotionPref>("system");
  const [osReduceMotion, setOsReduceMotion] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(MQ);
    setPreferenceState(getStoredPref());
    setOsReduceMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) => setOsReduceMotion(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const shouldReduceMotion = useMemo(() => {
    if (preference === "system") return osReduceMotion;
    return preference === "reduce";
  }, [preference, osReduceMotion]);

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-reduce-motion",
      shouldReduceMotion ? "true" : "false",
    );
  }, [shouldReduceMotion]);

  const setPreference = useCallback((pref: MotionPref) => {
    setPreferenceState(pref);
    if (pref === "system") {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, pref);
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(shouldReduceMotion ? "no-preference" : "reduce");
  }, [shouldReduceMotion, setPreference]);

  const fmReducedMotion = useMemo(() => {
    if (preference === "system") return "user" as const;
    return preference === "reduce"
      ? ("always" as const)
      : ("never" as const);
  }, [preference]);

  const value = useMemo(
    () => ({ shouldReduceMotion, preference, toggle, setPreference }),
    [shouldReduceMotion, preference, toggle, setPreference],
  );

  return (
    <Ctx.Provider value={value}>
      <MotionConfig reducedMotion={fmReducedMotion}>{children}</MotionConfig>
    </Ctx.Provider>
  );
}

export function useMotionPreference() {
  return useContext(Ctx);
}
